import { api } from './api';
import type { Item, ItemType, CreateItemRequest, UpdateItemRequest } from '@/types/item';
import type { PaginatedResponse } from '@/types/api';

interface GetAllParams {
  vaultId?: string;
  folderId?: string;
  type?: ItemType;
  deleted?: boolean;
  favorite?: boolean;
  page?: number;
  limit?: number;
}

export const itemService = {
  getStats: () =>
    api.get<{
      total: number;
      logins: number;
      cards: number;
      notes: number;
      identities: number;
    }>('/items/stats'),

  getAll: (params?: GetAllParams) => {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
    }

    const query = searchParams.toString();
    return api.get<PaginatedResponse<Item>>(`/items${query ? `?${query}` : ''}`);
  },

  getOne: (id: string) => api.get<Item>(`/items/${id}`),

  create: (data: CreateItemRequest) => api.post<Item>('/items', data),

  update: (id: string, data: UpdateItemRequest) =>
    api.patch<Item>(`/items/${id}`, data),

  delete: (id: string) => api.delete(`/items/${id}`),

  restore: (id: string) => api.post(`/items/${id}/restore`),

  permanentDelete: (id: string) => api.delete(`/items/${id}/permanent`),

  bulkMove: (itemIds: string[], folderId: string | null) =>
    api.patch<{ updated: number; failed: { id: string; error: string }[] }>(
      '/items/bulk/move',
      { itemIds, folderId }
    ),

  bulkDelete: (itemIds: string[]) =>
    api.post<{ deleted: number; failed: { id: string; error: string }[] }>(
      '/items/bulk/delete',
      { itemIds }
    ),

  bulkRestore: (itemIds: string[]) =>
    api.post<{ restored: number; failed: { id: string; error: string }[] }>(
      '/items/bulk/restore',
      { itemIds }
    ),

  getHistory: (id: string) =>
    api.get<{ data: Array<{ id: string; dataEncrypted: string; createdAt: string }> }>(
      `/items/${id}/history`
    ),
};
